import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Pagination,
  Checkbox,
  FormControlLabel,
  TableSortLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import { patternsApi } from '../services/api';
import { PatternPhrase, Page, BatchAddResult, BatchDeleteResult } from '../types/api';

const PatternsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [patterns, setPatterns] = useState<PatternPhrase[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Сортировка по фразе
  const [sortOrder, setSortOrder] = useState<'Asc' | 'Desc'>('Asc');

  // Фильтры
  const [search, setSearch] = useState('');
  const [whitelistFilter, setWhitelistFilter] = useState<'all' | 'whitelist' | 'blacklist'>('all');
  const searchRef = React.useRef<number | null>(null);

  // Выбранные элементы (по ID)
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  // Диалог добавления
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPatternsText, setNewPatternsText] = useState('');
  const [isWhitelist, setIsWhitelist] = useState(false);

  // Диалог удаления
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);

  const loadPatterns = async () => {
    setLoading(true);
    setError(null);
    try {
      const isInWhitelist =
        whitelistFilter === 'all'
          ? undefined
          : whitelistFilter === 'whitelist';

      const response: Page<PatternPhrase> = await patternsApi.getPatterns(
        page - 1,
        pageSize,
        'Phrase',
        sortOrder,
        search.trim() || undefined,
        isInWhitelist
      );
      setPatterns(response.items || []);
      setTotalCount(response.totalItemsCount || 0);
      setSelectedIds({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить шаблоны');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatterns();
  }, [page, sortOrder, whitelistFilter]);

  useEffect(() => {
    if (searchRef.current) {
      window.clearTimeout(searchRef.current);
    }
    searchRef.current = window.setTimeout(() => {
      setPage(1);
      loadPatterns();
    }, 300);
    return () => {
      if (searchRef.current) window.clearTimeout(searchRef.current);
    };
  }, [search]);

  const handleAddPatterns = async () => {
    if (!newPatternsText.trim()) return;

    try {
      const phrasesList = newPatternsText
        .split('\n')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      const itemsToAdd = phrasesList.map((phrase) => ({
        phrase,
        isInWhitelist: isWhitelist,
      }));

      const result: BatchAddResult = await patternsApi.addPatterns(itemsToAdd);
      setSuccess(`Добавлено: ${result.addedCount}, пропущено (дубликаты): ${result.skippedCount}`);
      setNewPatternsText('');
      setIsWhitelist(false);
      setAddDialogOpen(false);
      loadPatterns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить шаблоны');
    }
  };

  const handleDeletePatterns = async () => {
    try {
      const result: BatchDeleteResult = await patternsApi.removePatterns(idsToDelete);
      setSuccess(`Успешно удалено шаблонов: ${result.deletedCount}`);
      setIdsToDelete([]);
      setDeleteDialogOpen(false);
      loadPatterns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить шаблоны');
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allOnPageSelected = patterns.length > 0 && patterns.every((p) => selectedIds[p.id]);
  const someOnPageSelected = patterns.some(p => selectedIds[p.id]);

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      const copy = { ...selectedIds };
      patterns.forEach((p) => {
        delete copy[p.id];
      });
      setSelectedIds(copy);
    } else {
      const copy = { ...selectedIds };
      patterns.forEach((p) => {
        copy[p.id] = true;
      });
      setSelectedIds(copy);
    }
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'Asc' ? 'Desc' : 'Asc'));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Управление шаблонами</Typography>

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
            <TextField
              size="small"
              placeholder="Поиск по фразе"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
            {/* Селект для мобилок */}
            <FormControl size="small" fullWidth>
              <InputLabel id="whitelist-filter-mobile">Категория</InputLabel>
              <Select
                labelId="whitelist-filter-mobile"
                label="Категория"
                value={whitelistFilter}
                onChange={(e) => {
                  setWhitelistFilter(e.target.value as any);
                  setPage(1);
                }}
              >
                <MenuItem value="all">Все шаблоны</MenuItem>
                <MenuItem value="blacklist">Черный список</MenuItem>
                <MenuItem value="whitelist">Белый список</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
              >
                Добавить
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={!someOnPageSelected}
                onClick={() => {
                  const toDelete = patterns.filter((p) => selectedIds[p.id]).map((p) => p.id);
                  setIdsToDelete(toDelete);
                  setDeleteDialogOpen(true);
                }}
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
              >
                Удалить
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Поиск по фразе"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />

            {/* Селект для десктопа */}
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="whitelist-filter-desktop">Категория</InputLabel>
              <Select
                labelId="whitelist-filter-desktop"
                label="Категория"
                value={whitelistFilter}
                onChange={(e) => {
                  setWhitelistFilter(e.target.value as any);
                  setPage(1);
                }}
              >
                <MenuItem value="all">Все шаблоны</MenuItem>
                <MenuItem value="blacklist">Черный список</MenuItem>
                <MenuItem value="whitelist">Белый список</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Добавить шаблоны
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={!someOnPageSelected}
              onClick={() => {
                const toDelete = patterns.filter((p) => selectedIds[p.id]).map((p) => p.id);
                setIdsToDelete(toDelete);
                setDeleteDialogOpen(true);
              }}
            >
              Удалить выбранные
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardHeader
          avatar={<TextFieldsIcon />}
          title="Шаблоны"
          subheader={`Найдено: ${totalCount} шаблонов`}
        />
        <CardContent>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Checkbox
                  indeterminate={!allOnPageSelected && someOnPageSelected}
                  checked={allOnPageSelected}
                  onChange={toggleSelectAllOnPage}
                />
                <Typography variant="subtitle2">Выбрать все</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" variant="outlined" onClick={handleSortToggle}>
                  Фраза: {sortOrder === 'Asc' ? 'А-Я' : 'Я-А'}
                </Button>
              </Box>
              {patterns.map((pattern) => (
                <Paper key={pattern.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Checkbox
                      checked={!!selectedIds[pattern.id]}
                      onChange={() => toggleSelect(pattern.id)}
                      size="small"
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', mb: 1 }}>
                        {pattern.phrase}
                      </Typography>
                      {/* Метка для мобилок */}
                      <Chip
                        label={pattern.isInWhitelist ? 'Белый список' : 'Черный список'}
                        color={pattern.isInWhitelist ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        setIdsToDelete([pattern.id]);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={!allOnPageSelected && someOnPageSelected}
                        checked={allOnPageSelected}
                        onChange={toggleSelectAllOnPage}
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active
                        direction={sortOrder.toLowerCase() as 'asc' | 'desc'}
                        onClick={handleSortToggle}
                      >
                        Фраза
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Категория</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patterns.map((pattern) => (
                    <TableRow key={pattern.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selectedIds[pattern.id]}
                          onChange={() => toggleSelect(pattern.id)}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 450, wordBreak: 'break-word' }}>
                        {pattern.phrase}
                      </TableCell>
                      <TableCell>
                        {/* Метка в таблице */}
                        <Chip
                          label={pattern.isInWhitelist ? 'Белый список' : 'Черный список'}
                          color={pattern.isInWhitelist ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setIdsToDelete([pattern.id]);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {patterns.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        Шаблоны не найдены
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalCount > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: { xs: 1.5, sm: 2 } }}>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Patterns Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить новые шаблоны</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Шаблоны (по одному на строку)"
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            value={newPatternsText}
            onChange={(e) => setNewPatternsText(e.target.value)}
            placeholder="Введите фразы, по одной на строку..."
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isWhitelist}
                onChange={(e) => setIsWhitelist(e.target.checked)}
                color="success"
              />
            }
            label="Добавить эти фразы в Белый список"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleAddPatterns} variant="contained">
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Patterns Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Удалить шаблоны</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить {idsToDelete.length} шаблон(ов)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeletePatterns} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatternsPage;